import React, { useEffect, useMemo, useState } from "react";
import { message } from "antd";

import { FileDropzone, Input, Modal } from "../../../shared";
import type { FileRejectReason } from "../../../shared/FileDropzone/FileDropzone";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { getShortcutIconDisplaySrc } from "../../../../hooks/useShortcutIconSrc";
import type { ProfileShortcut } from "../../../../types/profileShortcuts";

import styles from "./stylesShortcutFormModal.module.css";

/**
 * Formats accepted by `POST .../profile-shortcuts/icon` (JPEG / JPG / PNG /
 * SVG, 512 KB). Extensions cover browsers that leave MIME blank on drop; MIME
 * tokens cover pickers that omit/normalize the extension. `image/jpg` is
 * non-standard but appears in the wild.
 */
export const SHORTCUT_ICON_ACCEPT = [
  ".jpeg",
  ".jpg",
  ".png",
  ".svg",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/svg+xml",
].join(",");

/** Mirrors the backend 512 KB cap on the profile-shortcut icon route. */
export const SHORTCUT_ICON_MAX_BYTES = 512 * 1024;

/** Lowercase letters, digits, underscores — no spaces or other punctuation. */
export const PROFILE_SHORTCUT_ID_PATTERN = /^[a-z0-9_]+$/;

/** Strips anything that isn't a valid profile-shortcut id character. */
export function sanitizeProfileShortcutId(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9_]/g, "");
}

export type ShortcutFormValues = {
  name: string;
  /**
   * Required on create — the stable id the client will key on. Omitted on
   * edit (ids are immutable once created).
   */
  id?: string;
  /** Newly picked icon file, or `null` when no new file was chosen. */
  iconFile: File | null;
  /**
   * `true` when the existing icon was removed and not replaced — the caller
   * must drop the stored asset. Without this, a bare `iconFile: null` can't
   * tell "kept the existing icon" apart from "cleared it".
   */
  clearIcon: boolean;
};

export type ShortcutFormModalProps = {
  open: boolean;
  /** When set, the modal is in edit mode and hydrates from this shortcut. */
  shortcut: ProfileShortcut | null;
  /**
   * IDs already in the shortcuts list — create mode rejects duplicates.
   * Ignored in edit mode.
   */
  existingIds?: ReadonlyArray<string>;
  onClose: () => void;
  onSubmit: (values: ShortcutFormValues) => void | Promise<void>;
};

const REJECT_COPY: Record<FileRejectReason, string> = {
  type: AppStrings.PAM_UPLOAD_ICON_SUPPORT_FORMATS,
  size: AppStrings.PAM_UPLOAD_ICON_TOO_LARGE,
};

/**
 * Create / Edit Feature modal for profile shortcuts (FE-2456). Create collects
 * name + unique id + optional icon; edit collects name + icon. Persist wiring
 * lives with the parent — this modal collects values, awaits `onSubmit` (e.g.
 * icon upload), and keeps Save/Add in a loading state until that promise
 * settles. The parent closes the modal only after a successful save.
 */
const ShortcutFormModal: React.FC<ShortcutFormModalProps> = ({
  open,
  shortcut,
  existingIds = [],
  onClose,
  onSubmit,
}) => {
  const isEdit = Boolean(shortcut);
  const [name, setName] = useState("");
  const [id, setId] = useState("");
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [clearedInitialIcon, setClearedInitialIcon] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const existingIdSet = useMemo(
    () => new Set(existingIds),
    [existingIds],
  );

  useEffect(() => {
    if (!open) {
      setIsSubmitting(false);
      return;
    }
    setName(shortcut?.name ?? "");
    setId("");
    setIconFile(null);
    setClearedInitialIcon(false);
    setIsSubmitting(false);
  }, [open, shortcut]);

  const initialIconUrl =
    clearedInitialIcon || !shortcut
      ? null
      : getShortcutIconDisplaySrc(shortcut);

  const trimmedName = name.trim();
  const isIdDuplicate = !isEdit && id.length > 0 && existingIdSet.has(id);
  const isIdFormatValid = isEdit || PROFILE_SHORTCUT_ID_PATTERN.test(id);
  const canSubmit = isEdit
    ? trimmedName.length > 0
    : trimmedName.length > 0 &&
      id.length > 0 &&
      isIdFormatValid &&
      !isIdDuplicate;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        name: trimmedName,
        ...(isEdit ? {} : { id }),
        iconFile,
        clearIcon: clearedInitialIcon && !iconFile,
      });
    } catch {
      // Parent surfaces upload/save errors; keep the modal open for retry.
    } finally {
      // On success the parent closes (`open` → false). On failure we stay open
      // and drop the loader so the operator can retry.
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isEdit ? AppStrings.PAM_EDIT_FEATURE : AppStrings.PAM_CREATE_FEATURE}
      size="md"
      className={styles.modal}
      destroyOnClose
      maskClosable={!isSubmitting}
      keyboard={!isSubmitting}
      data-testid="shortcut-form-modal"
      primaryAction={{
        label: isEdit ? AppStrings.SAVE : AppStrings.ADD,
        onClick: handleSubmit,
        loading: isSubmitting,
        disabled: !canSubmit || isSubmitting,
      }}
    >
      <div className={styles.body}>
        {!isEdit ? (
          <p className={styles.devNote} data-testid="shortcut-form-dev-note">
            {AppStrings.PAM_CREATE_FEATURE_DEV_NOTE}
          </p>
        ) : null}

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{AppStrings.NAME}</span>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={AppStrings.TEXT}
            data-testid="shortcut-form-name"
            aria-label={AppStrings.NAME}
            disabled={isSubmitting}
          />
        </label>

        {!isEdit ? (
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{AppStrings.PAM_FEATURE_ID}</span>
            <Input
              value={id}
              onChange={(event) =>
                setId(sanitizeProfileShortcutId(event.target.value))
              }
              placeholder={AppStrings.PAM_FEATURE_ID_PLACEHOLDER}
              data-testid="shortcut-form-id"
              aria-label={AppStrings.PAM_FEATURE_ID}
              aria-invalid={isIdDuplicate || undefined}
              aria-describedby="shortcut-form-id-hint"
              disabled={isSubmitting}
            />
            <span
              id="shortcut-form-id-hint"
              className={
                isIdDuplicate ? styles.fieldHintError : styles.fieldHint
              }
              data-testid="shortcut-form-id-hint"
            >
              {isIdDuplicate
                ? AppStrings.PAM_FEATURE_ID_DUPLICATE
                : AppStrings.PAM_FEATURE_ID_HINT}
            </span>
          </label>
        ) : null}

        <div className={styles.field}>
          <span className={styles.fieldLabel}>{AppStrings.PAM_UPLOAD_ICON}</span>
          <FileDropzone
            value={iconFile}
            onChange={setIconFile}
            accept={SHORTCUT_ICON_ACCEPT}
            maxBytes={SHORTCUT_ICON_MAX_BYTES}
            initialUrl={initialIconUrl}
            onRemoveInitial={() => setClearedInitialIcon(true)}
            onReject={(_file, reason) => message.error(REJECT_COPY[reason])}
            heading={AppStrings.PAM_DRAG_N_DROP_TO_UPLOAD_IMAGE}
            hint={AppStrings.PAM_UPLOAD_ICON_SUPPORT_FORMATS}
            buttonLabel={AppStrings.BROWSE_FILE}
            inputAriaLabel={AppStrings.PAM_UPLOAD_ICON}
            className={styles.dropzone}
            data-testid="shortcut-form-icon"
            disabled={isSubmitting}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ShortcutFormModal;
